// External imports
import { createContext, type ReactElement } from 'react';
import { Outlet } from 'react-router-dom';

// Internal imports
import { Navigation } from './Navigation';
import { Footer } from './Footer';
import { CookieConsent } from './CookieConsent';
import { useTheme } from '@/app/hooks/useTheme';

type Theme = 'light' | 'dark';

interface ThemeContextValue {
  theme: Theme;
  toggleTheme: () => void;
}

export const ThemeContext = createContext<ThemeContextValue>({
  theme: 'dark',
  toggleTheme: () => {},
});

/**
 * SkipNavigation — "Skip to main content" link for keyboard users.
 * WCAG 2.1 Success Criterion 2.4.1 (Bypass Blocks)
 */
function SkipNavigation(): ReactElement {
  return (
    <a
      href="#main-content"
      className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-brand-sienna focus:text-white focus:rounded-md focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-brand-orange focus:ring-offset-2 transition-all"
    >
      Skip to main content
    </a>
  );
}

/**
 * Layout wraps page content with Navigation, Footer, and theme provider.
 */
export function Layout(): ReactElement {
  const { theme, toggleTheme } = useTheme();

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      <div className="min-h-screen flex flex-col">
        <SkipNavigation />
        <Navigation />
        <div id="main-content" className="flex-1 pt-16">
          <Outlet />
        </div>
        <Footer />
        <CookieConsent />
      </div>
    </ThemeContext.Provider>
  );
}
